import React, { useState, useMemo, useEffect } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { toast } from 'react-toastify';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import Swal from 'sweetalert2';
import useStore from '../../states/store';
import TabPanel from '../../components/TabPanel';
import Filter from './components/Filter';
import Table from './components/Table';
import useProductSamplingContext from './context/useProductSamplingContext';
import { DashboardProvider } from '../Dashboard/useDashboard';
import {
  getProductList,
  getProduct,
  postAddProduct,
  postUpdateProduct,
} from '../../api/productSamplingMaintenanceAPI';
import AddProductDetails from './components/AddProductDetailsModal';
import ProductDetailsModal from './components/ProductDetails';

export default function ProductSamplingMaintenance() {
  const auth = useStore((state) => state.auth);

  const defaultFilter = {
    department: { label: 'ALL', value: null },
    products: [],
  };

  const addProductDefault = {
    toggle: false,
    is_published: 0,
    products: [
      {
        id: 1,
        product_code: '',
        product_name: '',
        product_selected: { value: null, label: 'Select Product' },
        segments: [],
      },
    ],
  };
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [productsData, setProductsData] = useState([]);
  const [addProductModal, setAddProductModal] = useState(addProductDefault);
  const [updateProductModal, setUpdateProductModal] = useState({
    toggle: false,
    product: {},
  });
  const [filterForm, setFilterForm] = useState(defaultFilter);
  const [clearFilter, setClearFilter] = useState(false);
  const [productOptions, setProductOptions] = useState([]);

  const handleChangeActiveTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  const searchProductList = async () => {
    try {
      setLoading(true);

      const statusType = {
        department: filterForm?.department?.value,
        product_code: filterForm?.products.map((row) => row.value),
      };
      const response = await getProductList(statusType);
      const { code, data } = response.data;

      if (code === '200') {
        setProductsData(data);
      }

      setLoading(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const clearProductList = () => {
    setFilterForm(defaultFilter);
    setClearFilter(true);
  };

  const handleViewProduct = async (pcode) => {
    try {
      setUpdateProductModal({ ...updateProductModal, toggle: true });
      const response = await getProduct({ product_code: pcode });
      const { code, data } = response.data;

      if (code === '200') {
        setUpdateProductModal({
          ...updateProductModal,
          toggle: true,
          product: { ...data },
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddProductModal = () => {
    setAddProductModal({ ...addProductModal, toggle: true });
  };

  const handleProceedAddProduct = () => {
    try {
      Swal.fire({
        icon: 'warning',
        title: `Are you sure you want to add (${addProductModal?.products?.length}) products`,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: '<small style="color:#fff"> Confirm </small>',
        cancelButtonText: '<small style="color:#fff"> Cancel </small>',
      }).then((response) => {
        if (response.isConfirmed) {
          postAddProduct(addProductModal).then((res) => {
            const { code, message } = res.data;
            if (code === '200') {
              toast.success(message);
              setAddProductModal(addProductDefault);
              searchProductList();
            } else {
              Swal.fire({
                title: 'Invalid Request',
                html: message,
                icon: 'warning',
              });
            }
          });
        }
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddProduct = () => {
    try {
      const errMessage = [];
      addProductModal?.products?.map((row, index) =>
        !row?.product_code
          ? errMessage.push(errMessage, `Please select product on line: ${index + 1}`)
          : '',
      );
      if (errMessage.length > 1) {
        Swal.fire({
          title: 'Invalid Request',
          html: errMessage.join('<br />'),
          icon: 'warning',
        });
      } else {
        handleProceedAddProduct();
      }
    } catch (error) {
      toast.error(error.message);
    }
  };
  const handleUpdateProduct = () => {
    try {
      Swal.fire({
        icon: 'warning',
        title: `Are you sure you want to update product`,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: '<small style="color:#fff"> Confirm </small>',
        cancelButtonText: '<small style="color:#fff"> Cancel </small>',
      }).then((response) => {
        if (response.isConfirmed) {
          postUpdateProduct(updateProductModal.product).then((res) => {
            const { code, message } = res.data;
            if (code === '200') {
              setUpdateProductModal({
                ...updateProductModal,
                toggle: false,
                product: {},
              });
              toast.success(message);
              searchProductList();
            } else {
              Swal.fire({
                title: 'Invalid Request',
                html: message,
                icon: 'warning',
              });
            }
          });
        }
      });
    } catch (error) {
      toast.error(error.message);
    }
  };
  useEffect(() => {
    searchProductList();
  }, [activeTab, clearFilter]);

  return (
    <DashboardProvider>
      <useProductSamplingContext.Provider
        value={useMemo(
          () => ({
            auth,
            activeTab,
            loading,
            filterForm,
            productsData,
            productOptions,
            addProductModal,
            updateProductModal,
            searchProductList,
            clearProductList,
            handleViewProduct,
            handleAddProductModal,
            handleAddProduct,
            handleUpdateProduct,
            setAddProductModal,
            setUpdateProductModal,
            setProductOptions,
            setFilterForm,
          }),
          [activeTab, filterForm, productsData, addProductModal, updateProductModal],
        )}
      >
        <Box sx={{ backgroundColor: '#fff', p: 1, boxShadow: 3 }}>
          <Filter />
        </Box>
        <Box padding={4}>
          <Box>
            <Box>
              <Grid container spacing={2}>
                <Grid xs={12} sm={12} md={12}>
                  <Box sx={{ backgroundColor: '#fff' }} mt={4}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs
                        value={activeTab}
                        onChange={handleChangeActiveTab}
                        aria-label='basic tabs example'
                      >
                        <Tab
                          label='Product Sampling List'
                          id='products-list'
                          aria-controls='products-list'
                        />
                      </Tabs>
                    </Box>
                    <TabPanel value={activeTab} index={activeTab}>
                      <Table />
                    </TabPanel>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
        <AddProductDetails />
        <ProductDetailsModal />
      </useProductSamplingContext.Provider>
    </DashboardProvider>
  );
}
